/**
 * ExamNotificationsAudit — panel that surfaces the per-exam audit feed of
 * activation / closing-soon / closed emails sent through the central
 * notification engine.
 *
 * Backend (live since iter-350):
 *   GET /api/school/tenants/{tid}/exams/{eid}/notifications
 *
 * Drop into the admin Exam Detail view. Shows:
 *   - Tally by trigger (activated / closing_soon / closed).
 *   - The 20 most recent rows with sent_at + result + error if any.
 *   - "Re-fire activation" and "Fire closed notification now" admin buttons
 *     that hit the existing /notify-activated and /notify-closed routes.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, Bell, BellOff, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

export default function ExamNotificationsAudit({ tenantId, examId }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['exam-notifications', tenantId, examId],
    queryFn: () => api.get(`/school/tenants/${tenantId}/exams/${examId}/notifications`).then(r => r.data),
    enabled: !!(tenantId && examId),
    refetchInterval: 60_000,
  });

  const refireActivated = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/exams/${examId}/notify-activated`).then(r => r.data),
    onSuccess: () => { toast.success('Activation emails queued'); refetch(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const fireClosed = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/exams/${examId}/notify-closed`).then(r => r.data),
    onSuccess: (d) => { toast.success(`Sent ${d?.sent ?? 0} · skipped ${d?.skipped ?? 0}`); refetch(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const summary = data?.summary || {};
  const logs = data?.logs || [];

  const triggerLabel = {
    exam_activated: 'Activation',
    exam_closing_soon: 'Closing soon',
    exam_closed: 'Closed',
  };
  const triggerColor = {
    exam_activated: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    exam_closing_soon: 'bg-amber-50 text-amber-700 border-amber-200',
    exam_closed: 'bg-slate-50 text-slate-700 border-slate-200',
  };

  return (
    <Card data-testid="exam-notifications-audit">
      <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-4 w-4 text-fuchsia-600" /> Notification Audit
        </CardTitle>
        <div className="flex items-center gap-1 flex-wrap">
          <Button size="sm" variant="outline" disabled={refireActivated.isPending} onClick={() => refireActivated.mutate()} data-testid="ena-refire-activated">
            <Bell className="h-3.5 w-3.5 mr-1" /> Re-fire activation
          </Button>
          <Button size="sm" variant="outline" disabled={fireClosed.isPending} onClick={() => fireClosed.mutate()} data-testid="ena-fire-closed">
            <BellOff className="h-3.5 w-3.5 mr-1" /> Fire closed
          </Button>
          <Button size="sm" variant="ghost" onClick={() => refetch()} data-testid="ena-refresh">
            <RefreshCcw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(triggerLabel).map(([k, label]) => (
            <div key={k} className={`border rounded p-2 text-center ${triggerColor[k]}`} data-testid={`ena-summary-${k}`}>
              <p className="text-xs uppercase tracking-wide">{label}</p>
              <p className="text-xl font-semibold">{summary[k] || 0}</p>
            </div>
          ))}
        </div>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
        ) : logs.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4 border border-dashed rounded" data-testid="ena-empty">
            No notifications sent for this exam yet.
          </p>
        ) : (
          <div className="text-xs space-y-1 max-h-60 overflow-auto" data-testid="ena-log-list">
            {logs.slice(0, 20).map(l => (
              <div key={l.id} className="flex items-center justify-between border-b py-1" data-testid={`ena-row-${l.id}`}>
                <span className="truncate flex-1">
                  <Badge variant="outline" className="text-[10px] mr-1">{triggerLabel[l.trigger] || l.trigger}</Badge>
                  {l.window && <Badge variant="outline" className="text-[10px] mr-1">{l.window}</Badge>}
                  <span className="font-mono">{l.student_id?.slice(-6) || '—'}</span>
                  {l.result?.email?.success === false && (
                    <span className="text-rose-600 ml-2">{l.result.email.error?.slice(0, 80)}</span>
                  )}
                </span>
                <span className="text-slate-400">{l.sent_at?.replace('T', ' ').slice(0, 19)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
